import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { 
  Search, 
  BarChart3, 
  Database, 
  Settings,
  ArrowRight,
  CheckCircle2,
  Clock,
  TrendingUp,
  Target,
  X,
  Users,
  Zap,
  ChevronRight,
  FileText
} from 'lucide-react';

const ProductsPage = () => {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [activePanelProduct, setActivePanelProduct] = useState('laizyleads');

  return (
    <div className="pt-20">
      <SEO
        title="KI-Produkte – LAIzyleads, LAIzyblueprint & Automatisierungstools für Unternehmen"
        description="Entdecken Sie unsere KI-Produkte: LAIzyleads für automatisierte Leadgenerierung, LAIzyblueprint für KI-gestützte Leistungsverzeichnisse und weitere KI-Tools. Made in Germany."
        path="/produkte"
        keywords="KI Produkte, LAIzyleads, LAIzyblueprint, KI Leadgenerierung, KI Leistungsverzeichnis, KI Tools Unternehmen, automatisierte Leadgenerierung, KI Software kaufen"
        breadcrumbs={[
          { name: 'Startseite', url: '/' },
          { name: 'Produkte' },
        ]}
      />
      <HeroSection />
      <ProductsGrid onOpenPanel={(product) => { setActivePanelProduct(product); setIsPanelOpen(true); }} />
      <HowWeHelp />
      <CTASection />
      
      {/* Slide Panels */}
      <ProspektAIPanel isOpen={isPanelOpen && activePanelProduct === 'laizyleads'} onClose={() => setIsPanelOpen(false)} />
      <BlueprintPanel isOpen={isPanelOpen && activePanelProduct === 'laizyblueprint'} onClose={() => setIsPanelOpen(false)} />
      <DocuPanel isOpen={isPanelOpen && activePanelProduct === 'laizydocu'} onClose={() => setIsPanelOpen(false)} />
    </div>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section className="section bg-gray-50 relative overflow-hidden">
      {/* Grid Pattern */}
      <div className="absolute inset-0 grid-pattern" />
      
      <div className="container-custom relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <div className="badge mb-6 fade-up">KI-Produkte für Unternehmen</div>
          <div className="reveal-container">
            <h1 className="heading-xl text-4xl md:text-5xl lg:text-6xl text-gray-900 reveal-text mb-6">
              KI-Tools für den Mittelstand — <span className="gradient-text">Prozesse automatisieren</span>, Kosten senken
            </h1>
          </div>
          <p className="body-text text-base md:text-lg text-gray-600 max-w-3xl mx-auto fade-up">
            Unsere KI-Lösungen wurden speziell für kleine und mittelständische Unternehmen in Deutschland entwickelt.
            Von automatisierter Lead-Generierung bis zur intelligenten Datenanalyse — praxisnah, DSGVO-konform und sofort einsatzbereit.
          </p>
        </div>
      </div>
    </section>
  );
};

// Products Grid
const ProductsGrid = ({ onOpenPanel }) => {
  const products = [
    {
      icon: Search,
      logo: '/logo-laizyleads.png',
      name: 'LAIzyleads',
      category: 'Lead-Generierung',
      shortDesc: 'KI-gestütztes Tool zur automatisierten Identifizierung qualifizierter B2B-Geschäftskontakte. Sparen Sie bis zu 70% Recherchezeit.',
      features: [
        'Intelligente Unternehmenssuche',
        'Automatisiertes Lead-Scoring',
        'Personalisierte Kontaktvorschläge',
      ],
      highlighted: true,
      panelId: 'laizyleads',
      externalUrl: 'https://laizyleads.de/',
    },
    {
      icon: FileText,
      logo: '/logo-laizyblueprint.png',
      name: 'LAIzyblueprint',
      category: 'Leistungsverzeichnis',
      shortDesc: 'KI-gestützte Erstellung von Leistungsverzeichnissen aus Grundrissen. Grundriss einlesen, bearbeiten und automatisch ein LV generieren.',
      features: [
        'Grundriss-Erkennung per KI',
        'Interaktive Bearbeitung',
        'Automatisches Leistungsverzeichnis',
      ],
      highlighted: true,
      panelId: 'laizyblueprint',
      externalUrl: 'https://www.laizyblueprint.de/',
    },
    {
      icon: BarChart3,
      logo: '/logo-laizydocu.png',
      name: 'LAIzydocu',
      category: 'Dokumentenmanagement',
      shortDesc: 'KI-gestütztes Dokumentenmanagement für Unternehmen. Dokumente intelligent erfassen, verarbeiten und verwalten — automatisiert und DSGVO-konform.',
      features: [
        'Intelligente Dokumentenerfassung',
        'Automatische Klassifizierung',
        'Smarte Datenextraktion',
      ],
      highlighted: true,
      panelId: 'laizydocu',
      externalUrl: 'https://laizydocu.de/',
    },
    {
      icon: Database,
      name: 'Data Insights',
      category: 'Datenanalyse',
      shortDesc: 'KI-gestützte Datenanalyse zur Gewinnung wertvoller Geschäftseinblicke. Muster erkennen, die Menschen übersehen.',
      features: [
        'Automatische Datenverarbeitung',
        'Muster-Erkennung',
        'Visualisierte Reports',
      ],
      highlighted: false,
    },
    {
      icon: Settings,
      name: 'Process AI',
      category: 'Automatisierung',
      shortDesc: 'Intelligente Automatisierung von Geschäftsprozessen — von Workflows über Dokumentenverarbeitung bis zur E-Mail-Automatisierung.',
      features: [
        'Workflow-Automatisierung',
        'Dokumentenverarbeitung',
        'E-Mail-Automatisierung',
      ],
      highlighted: false,
    },
  ];

  return (
    <section data-testid="products-grid-section" className="section bg-white">
      <div className="container-custom">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {products.map((product, index) => {
            const Icon = product.icon;
            return (
              <div 
                key={index}
                data-testid={`product-card-${index}`}
                className={`rounded-2xl p-8 transition-all duration-300 cursor-pointer group ${
                  product.highlighted 
                    ? 'tech-card-featured' 
                    : 'card'
                }`}
                onClick={() => product.panelId && onOpenPanel(product.panelId)}
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-6">
                  {product.logo ? (
                    <img 
                      src={product.logo} 
                      alt={`${product.name} Logo`} 
                      className="h-12 w-auto object-contain"
                    />
                  ) : (
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                      product.highlighted 
                        ? 'bg-gradient-to-br from-emerald-500 to-cyan-500' 
                        : 'bg-gradient-to-br from-emerald-100 to-cyan-100'
                    }`}>
                      <Icon className={`w-7 h-7 ${product.highlighted ? 'text-white' : 'text-emerald-600'}`} />
                    </div>
                  )}
                  {product.highlighted && (
                    <span className="text-xs font-semibold text-emerald-400 bg-emerald-400/10 px-3 py-1 rounded-full border border-emerald-400/20">
                      Empfohlen
                    </span>
                  )}
                </div>

                {/* Category */}
                <span className={`text-sm font-semibold uppercase tracking-wider ${product.highlighted ? 'text-emerald-400' : 'text-emerald-600'}`}>
                  {product.category}
                </span>

                {/* Title */}
                <h3 className={`font-['Space_Grotesk'] font-bold text-2xl mt-1 mb-3 ${
                  product.highlighted ? 'text-white' : 'text-gray-900'
                }`}>
                  {product.name}
                </h3>

                {/* Description */}
                <p className={`mb-6 ${product.highlighted ? 'text-gray-300' : 'text-gray-600'}`}>
                  {product.shortDesc}
                </p>

                {/* Features */}
                <ul className="space-y-3 mb-6">
                  {product.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <CheckCircle2 className={`w-5 h-5 flex-shrink-0 ${
                        product.highlighted ? 'text-emerald-400' : 'text-emerald-600'
                      }`} />
                      <span className={`text-sm ${product.highlighted ? 'text-gray-300' : 'text-gray-700'}`}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <div className="flex flex-col gap-2">
                  <div 
                    className={`inline-flex items-center gap-2 font-semibold transition-all group-hover:gap-3 ${
                      product.highlighted 
                        ? 'text-emerald-400' 
                        : 'text-emerald-600'
                    }`}
                  >
                    {product.panelId ? 'Details ansehen' : 'Mehr erfahren'}
                    <ChevronRight className="w-4 h-4" />
                  </div>
                  {product.externalUrl && (
                    <a
                      href={product.externalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      data-testid="product-external-link"
                      className="btn-primary text-sm w-fit mt-2"
                    >
                      <span>Zur Anwendung</span>
                      <ArrowRight className="w-4 h-4 relative z-10" />
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// LAIzyleads Slide Panel
const ProspektAIPanel = ({ isOpen, onClose }) => {
  const features = [
    {
      icon: Search,
      title: 'Intelligente Suche',
      description: 'Definieren Sie Ihre Zielkriterien und lassen Sie die KI passende Unternehmen finden.',
    },
    {
      icon: BarChart3,
      title: 'Lead-Scoring',
      description: 'Automatische Bewertung der Leads nach Relevanz und Kontaktpotenzial.',
    },
    {
      icon: Users,
      title: 'Kontaktvorschläge',
      description: 'Personalisierte Ansprechpartner mit Kontaktdaten und LinkedIn-Profilen.',
    },
    {
      icon: Zap,
      title: 'Echtzeit-Daten',
      description: 'Aktuelle Unternehmensinformationen durch kontinuierliche Datenaktualisierung.',
    },
  ];

  const benefits = [
    { value: '70%', label: 'Schnellere Lead-Recherche' },
    { value: '3x', label: 'Mehr qualifizierte Leads' },
    { value: '50%', label: 'Weniger Recherchezeit' },
  ];

  return (
    <>
      {/* Overlay */}
      <div 
        className={`slide-panel-overlay ${isOpen ? 'open' : ''}`}
        onClick={onClose}
      />
      
      {/* Panel */}
      <div 
        className={`slide-panel ${isOpen ? 'open' : ''}`}
        data-testid="prospekt-ai-panel"
      >
        <div className="p-6 md:p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-xl flex items-center justify-center">
                <Search className="w-6 h-6 text-white" />
              </div>
              <div>
                <span className="text-sm text-emerald-600 font-medium">Lead-Generierung</span>
                <h2 className="font-['Space_Grotesk'] font-bold text-2xl text-gray-900">
                  LAIzyleads
                </h2>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="w-10 h-10 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:border-gray-300 transition-colors"
              data-testid="close-panel"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Description */}
          <p className="text-gray-600 text-lg mb-8">
            LAIzyleads ist ein KI-gestütztes Tool, mit dem Unternehmen schnell 
            und effizient passende Geschäftspartner, Kunden oder Leads finden können. 
            Sparen Sie wertvolle Zeit bei der Recherche und konzentrieren Sie sich 
            auf das, was zählt: den persönlichen Kontakt.
          </p>

          {/* Screenshots */}
          <div className="mb-8">
            <h3 className="font-['Space_Grotesk'] font-semibold text-lg text-gray-900 mb-4">
              So funktioniert LAIzyleads
            </h3>
            <div className="space-y-4">
              <div className="rounded-xl overflow-hidden border border-gray-200 shadow-lg">
                <img 
                  src="https://customer-assets.emergentagent.com/job_motion-design-studio-1/artifacts/ux4qylck_Screenshot%202026-03-02%20150739.png"
                  alt="LAIzyleads - Kundenrecherche Dashboard"
                  className="w-full h-auto"
                  data-testid="panel-screenshot-1"
                />
              </div>
              <div className="rounded-xl overflow-hidden border border-gray-200 shadow-lg">
                <img 
                  src="https://customer-assets.emergentagent.com/job_motion-design-studio-1/artifacts/i81d0iah_Screenshot%202026-03-02%20150810.png"
                  alt="LAIzyleads - Lead Details mit Matching-Score"
                  className="w-full h-auto"
                  data-testid="panel-screenshot-2"
                />
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="mb-8">
            <h3 className="font-['Space_Grotesk'] font-semibold text-lg text-gray-900 mb-4">
              Funktionen
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div key={index} className="p-4 rounded-xl bg-gray-50 border border-gray-100">
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center mb-3">
                      <Icon className="w-5 h-5 text-emerald-600" />
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-1">{feature.title}</h4>
                    <p className="text-sm text-gray-600">{feature.description}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Benefits */}
          <div className="mb-8 p-6 rounded-xl bg-gradient-to-br from-emerald-50 to-cyan-50 border border-emerald-100">
            <h3 className="font-['Space_Grotesk'] font-semibold text-lg text-gray-900 mb-4">
              Ihre Vorteile
            </h3>
            <div className="grid grid-cols-3 gap-4">
              {benefits.map((benefit, index) => (
                <div key={index} className="text-center">
                  <div className="text-2xl font-bold text-emerald-600 mb-1">{benefit.value}</div>
                  <div className="text-xs text-gray-600">{benefit.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <a
            href="https://laizyleads.de/"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary w-full justify-center text-base"
            data-testid="panel-cta"
          >
            <span>Zur Anwendung</span>
            <ArrowRight className="w-5 h-5 relative z-10" />
          </a>
        </div>
      </div>
    </>
  );
};

// LAIzyblueprint Slide Panel
const BlueprintPanel = ({ isOpen, onClose }) => {
  const features = [
    {
      icon: FileText,
      title: 'Grundriss einlesen',
      description: 'Laden Sie Ihren Grundriss hoch und die KI erkennt automatisch Räume, Maße und Strukturen.',
    },
    {
      icon: Settings,
      title: 'Interaktiv bearbeiten',
      description: 'Passen Sie erkannte Elemente an, fügen Sie Details hinzu oder korrigieren Sie Abweichungen.',
    },
    {
      icon: Database,
      title: 'LV generieren',
      description: 'Automatische Erstellung eines Leistungsverzeichnisses basierend auf dem bearbeiteten Grundriss.',
    },
    {
      icon: Zap,
      title: 'Export & Weiterverarbeitung',
      description: 'Exportieren Sie das fertige LV zur Weiterverarbeitung in Ihren bestehenden Workflow.',
    },
  ];

  return (
    <>
      <div 
        className={`slide-panel-overlay ${isOpen ? 'open' : ''}`}
        onClick={onClose}
      />
      <div 
        className={`slide-panel ${isOpen ? 'open' : ''}`}
        data-testid="blueprint-panel"
      >
        <div className="p-6 md:p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <img src="/logo-laizyblueprint.png" alt="LAIzyblueprint Logo" className="h-10 w-auto object-contain" />
              <div>
                <span className="text-sm text-emerald-600 font-medium">Leistungsverzeichnis</span>
                <h2 className="font-['Space_Grotesk'] font-bold text-2xl text-gray-900">
                  LAIzyblueprint
                </h2>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="w-10 h-10 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:border-gray-300 transition-colors"
              data-testid="close-blueprint-panel"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Description */}
          <p className="text-gray-600 text-lg mb-8">
            LAIzyblueprint ist eine KI-gestützte Engine, mit der Sie Grundrisse einlesen, 
            interaktiv bearbeiten und daraus automatisch ein professionelles Leistungsverzeichnis 
            erstellen können. Ideal für Architekten, Bauunternehmen und Projektleiter.
          </p>

          {/* Features */}
          <div className="mb-8">
            <h3 className="font-['Space_Grotesk'] font-semibold text-lg text-gray-900 mb-4">
              So funktioniert LAIzyblueprint
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div key={index} className="p-4 rounded-xl bg-gray-50 border border-gray-100">
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center mb-3">
                      <Icon className="w-5 h-5 text-emerald-600" />
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-1">{feature.title}</h4>
                    <p className="text-sm text-gray-600">{feature.description}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Benefits */}
          <div className="mb-8 p-6 rounded-xl bg-gradient-to-br from-emerald-50 to-cyan-50 border border-emerald-100">
            <h3 className="font-['Space_Grotesk'] font-semibold text-lg text-gray-900 mb-4">
              Ihre Vorteile
            </h3>
            <div className="grid grid-cols-3 gap-4">
              {[
                { value: '80%', label: 'Schnellere LV-Erstellung' },
                { value: 'KI', label: 'Automatische Erkennung' },
                { value: '100%', label: 'Anpassbar' },
              ].map((benefit, index) => (
                <div key={index} className="text-center">
                  <div className="text-2xl font-bold text-emerald-600 mb-1">{benefit.value}</div>
                  <div className="text-xs text-gray-600">{benefit.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <a
            href="https://www.laizyblueprint.de/"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary w-full justify-center text-base"
            data-testid="blueprint-panel-cta"
          >
            <span>Zur Anwendung</span>
            <ArrowRight className="w-5 h-5 relative z-10" />
          </a>
        </div>
      </div>
    </>
  );
};

// LAIzydocu Slide Panel
const DocuPanel = ({ isOpen, onClose }) => {
  const features = [
    {
      icon: FileText,
      title: 'Dokumente erfassen',
      description: 'Laden Sie Dokumente hoch — die KI erkennt automatisch Typ, Inhalt und relevante Datenfelder.',
    },
    {
      icon: Search,
      title: 'Automatische Klassifizierung',
      description: 'Rechnungen, Verträge, Lieferscheine — jedes Dokument wird intelligent kategorisiert und zugeordnet.',
    },
    {
      icon: Database,
      title: 'Datenextraktion',
      description: 'Relevante Informationen werden automatisch extrahiert und strukturiert aufbereitet.',
    },
    {
      icon: Settings,
      title: 'Workflow-Integration',
      description: 'Nahtlose Einbindung in bestehende Geschäftsprozesse und Systeme.',
    },
  ];

  return (
    <>
      <div 
        className={`slide-panel-overlay ${isOpen ? 'open' : ''}`}
        onClick={onClose}
      />
      <div 
        className={`slide-panel ${isOpen ? 'open' : ''}`}
        data-testid="docu-panel"
      >
        <div className="p-6 md:p-8">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <img src="/logo-laizydocu.png" alt="LAIzydocu Logo" className="h-10 w-auto object-contain" />
              <div>
                <span className="text-sm text-emerald-600 font-medium">Dokumentenmanagement</span>
                <h2 className="font-['Space_Grotesk'] font-bold text-2xl text-gray-900">
                  LAIzydocu
                </h2>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="w-10 h-10 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:border-gray-300 transition-colors"
              data-testid="close-docu-panel"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <p className="text-gray-600 text-lg mb-8">
            LAIzydocu ist ein KI-gestütztes Dokumentenmanagementsystem, das Unternehmen hilft, 
            ihre Dokumente intelligent zu erfassen, automatisch zu klassifizieren und relevante 
            Daten zu extrahieren. Weniger manueller Aufwand, mehr Effizienz — DSGVO-konform.
          </p>

          <div className="mb-8">
            <h3 className="font-['Space_Grotesk'] font-semibold text-lg text-gray-900 mb-4">
              So funktioniert LAIzydocu
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div key={index} className="p-4 rounded-xl bg-gray-50 border border-gray-100">
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center mb-3">
                      <Icon className="w-5 h-5 text-emerald-600" />
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-1">{feature.title}</h4>
                    <p className="text-sm text-gray-600">{feature.description}</p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mb-8 p-6 rounded-xl bg-gradient-to-br from-emerald-50 to-cyan-50 border border-emerald-100">
            <h3 className="font-['Space_Grotesk'] font-semibold text-lg text-gray-900 mb-4">
              Ihre Vorteile
            </h3>
            <div className="grid grid-cols-3 gap-4">
              {[
                { value: '90%', label: 'Weniger manuelle Erfassung' },
                { value: 'KI', label: 'Automatische Erkennung' },
                { value: 'DSGVO', label: 'Konform & sicher' },
              ].map((benefit, index) => (
                <div key={index} className="text-center">
                  <div className="text-2xl font-bold text-emerald-600 mb-1">{benefit.value}</div>
                  <div className="text-xs text-gray-600">{benefit.label}</div>
                </div>
              ))}
            </div>
          </div>

          <a
            href="https://laizydocu.de/"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary w-full justify-center text-base"
            data-testid="docu-panel-cta"
          >
            <span>Zur Anwendung</span>
            <ArrowRight className="w-5 h-5 relative z-10" />
          </a>
        </div>
      </div>
    </>
  );
};

// How We Help Section
const HowWeHelp = () => {
  const helpItems = [
    {
      icon: Clock,
      title: 'Zeit sparen',
      description: 'Automatisieren Sie repetitive Aufgaben und gewinnen Sie wertvolle Zeit für strategische Aktivitäten.',
    },
    {
      icon: TrendingUp,
      title: 'Kosten reduzieren',
      description: 'Senken Sie operative Kosten durch intelligente Automatisierung und effiziente Prozesse.',
    },
    {
      icon: Target,
      title: 'Bessere Entscheidungen',
      description: 'Treffen Sie fundierte Geschäftsentscheidungen basierend auf KI-gestützten Analysen.',
    },
  ];

  return (
    <section className="section bg-gray-50">
      <div className="container-custom">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="badge mb-4 fade-up">Ihr Mehrwert durch KI</div>
          <div className="reveal-container">
            <h2 className="heading-lg text-3xl md:text-4xl text-gray-900 reveal-text mb-4">
              So steigern KI-Tools <span className="gradient-text">Ihre Wettbewerbsfähigkeit</span>
            </h2>
          </div>
          <p className="body-text text-base md:text-lg text-gray-600 fade-up">
            Unternehmen, die KI-Automatisierung einsetzen, senken operative Kosten um bis zu 50% und gewinnen wertvolle Zeit für strategische Aufgaben.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {helpItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <div key={index} className="text-center animate-card">
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-100 to-cyan-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Icon className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="font-['Space_Grotesk'] font-semibold text-xl text-gray-900 mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-600">
                  {item.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => {
  return (
    <section className="section bg-white pb-24 md:pb-32">
      <div className="container-custom">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0A0F14] to-[#111820] p-8 md:p-12 lg:p-16">
          {/* Background Pattern */}
          <div className="absolute inset-0 grid-pattern opacity-10" />
          
          <div className="relative z-10 text-center max-w-3xl mx-auto">
            <h2 className="font-['Space_Grotesk'] font-bold text-3xl md:text-4xl text-white mb-6">
              Welche <span className="text-emerald-400">KI-Lösung</span> passt zu Ihrem Unternehmen?
            </h2>
            <p className="text-gray-400 text-base md:text-lg mb-8">
              Nutzen Sie unser kostenloses Erstgespräch: Wir analysieren Ihre Prozesse und empfehlen die optimale KI-Lösung für Ihren individuellen Bedarf — unverbindlich und DSGVO-konform.
            </p>
            <Link
              to="/#kontakt"
              className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold px-8 py-4 rounded-xl hover:from-emerald-600 hover:to-cyan-600 transition-all"
            >
              Kostenlose Beratung vereinbaren
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductsPage;
